import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#F7F6F2]/85 backdrop-blur-xl border-b border-[#2C3C30]/10"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <img
            src="/logo.png"
            alt="Adya Nutrition logo"
            className="h-12 w-12 md:h-14 md:w-14 object-contain transition-transform duration-500 group-hover:rotate-6"
          />
          <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#2C3C30] leading-none">
            Adya<span className="text-[#7D8C7A]">.</span>
            <span className="block text-[10px] tracking-[0.4em] uppercase font-sans text-[#566158] mt-0.5">
              Nutrition
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-sans text-sm tracking-wide uppercase transition-colors duration-300 ${
                  isActive
                    ? "text-[#2C3C30]"
                    : "text-[#566158] hover:text-[#2C3C30]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-book-cta"
            className="group relative overflow-hidden rounded-full bg-[#2C3C30] px-6 py-2.5 text-sm font-medium text-[#F7F6F2] transition-colors duration-300 hover:bg-[#1A211D]"
          >
            Book a Consultation
          </Link>
        </nav>

        <button
          className="md:hidden text-[#2C3C30]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-[#2C3C30]/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-serif text-3xl text-[#2C3C30]"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-2 rounded-full bg-[#2C3C30] px-6 py-3 text-center text-[#F7F6F2]"
                data-testid="mobile-book-cta"
              >
                Book a Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
